import React, {
    createContext,
    Dispatch,
    useContext,
    useEffect,
    useMemo,
    useReducer,
} from 'react'
import { useAppSelector } from 'store/useStoreHook'
import {
    usePosDependencies,
    TUsePosDependenciesType,
} from './usePosDependencies'
import { useApplyLoggerDispatch } from 'utilities/debug/logUtil'
import { OrderChannel } from 'constant/OrderChannels'
import { LogoutAfterPaymentOption } from 'models'
import { useCallerIdHubContext } from 'hooks/usePOSHubContext/usePOSHubContext'

type IPosContext = TUsePosDependenciesType & {
    isServiceChargeLoaded: boolean
    isOffline: boolean
    allMenuSearchable: boolean
    //menu view setting
    showOnlineStores: boolean
    showBrandFilter: boolean
    /**
     * When `TRUE`, the product will be allowed without time availbility restriction.
     * When `FALSE`, the time availabity check is applied to product when added. The error message box will be displayed when the user added the product which is not available at that time.
     */
    showTimeAvailabilityOverrideFilter: boolean
    allowToUseOpenItem: boolean
    //module
    isGiftCardEnabled: boolean
    useTip: boolean
    useServiceCharge: boolean
    //operational setting
    isKDSEnable: boolean
    isSendToKDSAvailable: boolean
    //sale process rule
    disableProductSelectionForOutOfStockItems: boolean
    disableProductSelectionForOutOfStockIngredients: boolean

    /**
     * When `TRUE`, the time availabity check will be applied, and the unsatisfied product will be hidden from the product panel / price selection dialog / etc.
     * When `FALSE`, product will always show even the time availability is not satisfied.
     */
    strictTimeAvailabilityOnSales: boolean
    isCalculateCompAfterDiscount: boolean

    defaultCustomerID: string | undefined
    logoutAfterPayment: LogoutAfterPaymentOption

    // calculated flag (derived from aboved flag)
    /**
     * Utility flag calculated from `!showTimeAvailabilityOverrideFilter && strictTimeAvailabilityOnSales`. Useful to determine hiding the product
     */
    shouldValidateTimeAvailabityForProduct: boolean
}

const initialState: IPosContext = {
    isServiceChargeLoaded: false,
    isOffline: false,

    /** TRUE if dependency is still loading, processing */
    loading: false,

    serviceChargeSetting: undefined,
    taxes: [],
    taxOptions: [],
    companySetting: undefined,
    fulfillmentTypes: [],

    pointRedemptionSetting: {},

    showOnlineStores: false,
    showBrandFilter: false,
    showTimeAvailabilityOverrideFilter: false,
    allowToUseOpenItem: false,
    isKDSEnable: false,
    isSendToKDSAvailable: false,

    voidReasons: [],
    strictTimeAvailabilityOnSales: true,
    isCalculateCompAfterDiscount: true,
    allMenuSearchable: false,
    useTip: false,
    useServiceCharge: false,
    disableProductSelectionForOutOfStockIngredients: false,
    disableProductSelectionForOutOfStockItems: false,
    logoutAfterPayment: LogoutAfterPaymentOption.Disable,
    isGiftCardEnabled: false,
    defaultCustomerID: undefined,

    shouldValidateTimeAvailabityForProduct: true,
}

export type PosGlobalAction = {
    type: 'ON_LOADED_SERVICE_CHARGE'
}

const posGlobalReducer = (
    state: IPosContext,
    action: PosGlobalAction,
): IPosContext => {
    switch (action.type) {
        case 'ON_LOADED_SERVICE_CHARGE':
            return {
                ...state,
                isServiceChargeLoaded: true,
            }
        default:
            return state
    }
}

interface IPosContextWithAction extends IPosContext {
    posGlobalActions: Dispatch<PosGlobalAction>
}

export const PosContext = createContext<IPosContextWithAction>(
    {} as IPosContextWithAction,
)

export function PosProvider({
    orderChannel,
    children,
}: {
    children: React.ReactNode
    orderChannel: OrderChannel
}) {
    const [state, dispatch] = useReducer(posGlobalReducer, initialState)
    const dispatchFunc = useApplyLoggerDispatch(
        'PosGlobalAction',
        dispatch,
        state,
    )
    const posSettingDepedencies = usePosDependencies(dispatchFunc)
    const isOffline = useAppSelector((state) => state.ui.appState.isOffline)

    const operationLoading = useAppSelector((state) => state.operation.loading)

    const {
        allowToSearchAcrossAllMenus,
        allowToUseOpenItemOnTableService,
        allowToUseOpenItemOnQuickService,
        showBrandFilterOnQuickService,
        showBrandFilterOnTableService,
        showOnlineStoresOnQuickService,
        showOnlineStoresOnTableService,
        showTimeAvailabilityOverrideFilterOnQuickService,
        showTimeAvailabilityOverrideFilterOnTableService,
        strictEnforcementOfTimeAvailabilityonSales,
        disableProductSelectionForOutOfStockIngredients,
        disableProductSelectionForOutOfStockItems,
        isActiveKitchenIntegrationAndEvent: isSendToKDSAvailable,
        kds: isKDSEnable,
        logoutAfterPaymentOnQuickService,
        logoutAfterPaymentOnTableService,
        giftCard,
        useTipsOnQuickService,
        useTipsOnTableService,
        isCalculateCompAfterDiscount,
        generalGlobalPOSDefaultCustomerID,
        quickServiceGlobalPOSDefaultCustomerID,
        tableServiceGlobalPOSDefaultCustomerID,
        generalServiceCharge,
        quickSalesServiceCharge,
        tableSalesServiceCharge,
    } = useAppSelector((state) => state.operation.data)

    const posSetting = useMemo(
        () =>
            orderChannel === OrderChannel.QuickService
                ? {
                      allowToUseOpenItem:
                          allowToUseOpenItemOnQuickService ?? false,
                      showBrandFilter: showBrandFilterOnQuickService ?? false,
                      showOnlineStores: showOnlineStoresOnQuickService ?? false,
                      showTimeAvailabilityOverrideFilter:
                          showTimeAvailabilityOverrideFilterOnQuickService ??
                          false,
                      logoutAfterPayment:
                          logoutAfterPaymentOnQuickService ??
                          LogoutAfterPaymentOption.Disable,
                      useTip: useTipsOnQuickService ?? false,
                      defaultCustomerID:
                          quickServiceGlobalPOSDefaultCustomerID ??
                          generalGlobalPOSDefaultCustomerID ??
                          undefined,
                      useServiceCharge:
                          !!quickSalesServiceCharge && !!generalServiceCharge,
                  }
                : {
                      allowToUseOpenItem:
                          allowToUseOpenItemOnTableService ?? false,
                      showBrandFilter: showBrandFilterOnTableService ?? false,
                      showOnlineStores: showOnlineStoresOnTableService ?? false,
                      showTimeAvailabilityOverrideFilter:
                          showTimeAvailabilityOverrideFilterOnTableService ??
                          false,
                      logoutAfterPayment:
                          logoutAfterPaymentOnTableService ??
                          LogoutAfterPaymentOption.Disable,
                      useTip: useTipsOnTableService ?? false,
                      defaultCustomerID:
                          tableServiceGlobalPOSDefaultCustomerID ??
                          generalGlobalPOSDefaultCustomerID!,
                      useServiceCharge:
                          !!tableSalesServiceCharge && !!generalServiceCharge,
                  },
        [
            allowToUseOpenItemOnQuickService,
            allowToUseOpenItemOnTableService,
            generalGlobalPOSDefaultCustomerID,
            generalServiceCharge,
            logoutAfterPaymentOnQuickService,
            logoutAfterPaymentOnTableService,
            orderChannel,
            quickSalesServiceCharge,
            quickServiceGlobalPOSDefaultCustomerID,
            showBrandFilterOnQuickService,
            showBrandFilterOnTableService,
            showOnlineStoresOnQuickService,
            showOnlineStoresOnTableService,
            showTimeAvailabilityOverrideFilterOnQuickService,
            showTimeAvailabilityOverrideFilterOnTableService,
            tableSalesServiceCharge,
            tableServiceGlobalPOSDefaultCustomerID,
            useTipsOnQuickService,
            useTipsOnTableService,
        ],
    )

    const { loadActiveCallerIdMessages } = useCallerIdHubContext()

    useEffect(() => {
        loadActiveCallerIdMessages()
    }, [loadActiveCallerIdMessages])

    return (
        <PosContext.Provider
            value={{
                ...state,
                ...posSettingDepedencies,
                ...posSetting,
                posGlobalActions: dispatchFunc,
                loading: posSettingDepedencies.loading || operationLoading,
                isOffline,
                isKDSEnable: isKDSEnable ?? false,
                isSendToKDSAvailable: isSendToKDSAvailable ?? false,
                isCalculateCompAfterDiscount:
                    isCalculateCompAfterDiscount ?? true,
                disableProductSelectionForOutOfStockIngredients:
                    disableProductSelectionForOutOfStockIngredients ?? false,
                disableProductSelectionForOutOfStockItems:
                    disableProductSelectionForOutOfStockItems ?? false,
                strictTimeAvailabilityOnSales:
                    strictEnforcementOfTimeAvailabilityonSales ?? true,
                allMenuSearchable: allowToSearchAcrossAllMenus ?? false,
                isGiftCardEnabled: giftCard ?? false,
                shouldValidateTimeAvailabityForProduct:
                    !posSetting.showTimeAvailabilityOverrideFilter &&
                    (strictEnforcementOfTimeAvailabilityonSales ?? true),
            }}
        >
            {children}
        </PosContext.Provider>
    )
}

export const usePosContext = () => useContext(PosContext)
